import { useCallback } from 'react';
import { normalizeQuestionConditionals, sanitizeConditionalConfig } from '../utils/questionnaireEditorUtils';

export function useQuestionsCRUD({
  questions,
  defaultQuestions, setDefaultQuestions,
  questionPages, selectedPageId,
  applyEditorStructure,
  error, setError
}) {
  const areQuestionsInSameScope = useCallback((question, parentQuestion) => {
    const questionPageId = question?.pageId || null;
    const parentPageId = parentQuestion?.pageId || null;
    return questionPageId === parentPageId;
  }, []);

  const placeQuestionAfterDependency = useCallback((sourceQuestions = [], questionId, dependencyQuestionId) => {
    const questionList = [...sourceQuestions];
    const questionIndex = questionList.findIndex((question) => String(question.id) === String(questionId));
    const dependencyIndex = questionList.findIndex((question) => String(question.id) === String(dependencyQuestionId));
    if (questionIndex === -1 || dependencyIndex === -1) return questionList;
    if (questionIndex === dependencyIndex + 1) return questionList;

    const questionToMove = questionList[questionIndex];
    const dependencyQuestion = questionList[dependencyIndex];
    if (!areQuestionsInSameScope(questionToMove, dependencyQuestion)) return questionList;

    const [movedQuestion] = questionList.splice(questionIndex, 1);
    const updatedDependencyIndex = questionList.findIndex((question) => String(question.id) === String(dependencyQuestionId));
    if (updatedDependencyIndex === -1) return [...questionList, movedQuestion];

    questionList.splice(updatedDependencyIndex + 1, 0, movedQuestion);
    return questionList;
  }, [areQuestionsInSameScope]);

  const addQuestion = (type = 'text') => {
    const needsOptions = type === 'single-choice' || type === 'multiple-choice' || type === 'ranking' || type === 'matrix' || type === 'combobox';
    const hasSelectedQuestionPage = questionPages.some((page) => page.id === selectedPageId);
    const fallbackPageId = hasSelectedQuestionPage ? selectedPageId : questionPages[0]?.id;
    if (!fallbackPageId) {
      setError('Es ist keine Frage-Seite verfügbar.');
      return;
    }
    const newQuestion = {
      id: Date.now(),
      type,
      text: '',
      description: '',
      pageId: fallbackPageId,
      answerType: type === 'image' ? 'text' : undefined,
      wordLimit: type === 'text' ? 'no-limit' : undefined,
      options: needsOptions ? (type === 'ranking' ? ['', ''] : ['']) : [],
      rowPlaceholder: type === 'matrix' ? 'Eintrag' : undefined,
      matrixMode: type === 'matrix' ? 'predefined' : undefined,
      rowLabels: type === 'matrix' ? [] : undefined,
      matrixAllowAdditionalRows: type === 'matrix' ? false : undefined,
      suggestions: type === 'combobox' ? [] : undefined,
      rowInputLabel: type === 'combobox' ? '' : undefined,
      columnLabel: type === 'combobox' ? '' : undefined,
      comboboxMode: type === 'combobox' ? 'predefined' : undefined,
      predefinedEntries: type === 'combobox' ? [] : undefined,
      comboboxAllowAdditionalEntries: type === 'combobox' ? false : undefined,
      acceptedFileTypes: type === 'file-upload' ? ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'] : undefined,
      maxFileSize: type === 'file-upload' ? 10 : undefined,
      requireNote: type === 'file-upload' ? false : undefined
    };
    applyEditorStructure([...questions, newQuestion], questionPages);
    if (error) setError(null);
  };

  const updateQuestion = (id, newProps) => {
    const updatedQuestions = questions.map((q) => {
      if (q.id === id) {
        const updatedQuestion = { ...q, ...newProps };
        if (newProps.type && (newProps.type === 'single-choice' || newProps.type === 'multiple-choice' || newProps.type === 'ranking' || newProps.type === 'matrix' || newProps.type === 'combobox')) {
          if (!updatedQuestion.options || !Array.isArray(updatedQuestion.options)) {
            updatedQuestion.options = newProps.type === 'ranking' ? ['', ''] : [''];
          }
          if (newProps.type === 'matrix') {
            if (!updatedQuestion.rowPlaceholder) updatedQuestion.rowPlaceholder = 'Eintrag';
            if (!updatedQuestion.matrixMode) updatedQuestion.matrixMode = 'predefined';
            if (!Array.isArray(updatedQuestion.rowLabels)) updatedQuestion.rowLabels = [];
            if (typeof updatedQuestion.matrixAllowAdditionalRows !== 'boolean') updatedQuestion.matrixAllowAdditionalRows = false;
          }
          if (newProps.type === 'combobox') {
            if (!Array.isArray(updatedQuestion.suggestions)) updatedQuestion.suggestions = [];
            if (updatedQuestion.rowInputLabel == null) updatedQuestion.rowInputLabel = '';
            if (updatedQuestion.columnLabel == null) updatedQuestion.columnLabel = '';
            if (!updatedQuestion.rowPlaceholder) updatedQuestion.rowPlaceholder = 'Eingabe...';
            if (!updatedQuestion.comboboxMode) updatedQuestion.comboboxMode = 'predefined';
            if (!Array.isArray(updatedQuestion.predefinedEntries)) updatedQuestion.predefinedEntries = [];
            if (typeof updatedQuestion.comboboxAllowAdditionalEntries !== 'boolean') updatedQuestion.comboboxAllowAdditionalEntries = false;
          }
        }
        if (newProps.type === 'file-upload') {
          if (!Array.isArray(updatedQuestion.acceptedFileTypes)) updatedQuestion.acceptedFileTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'];
          if (typeof updatedQuestion.maxFileSize !== 'number') updatedQuestion.maxFileSize = 10;
          if (typeof updatedQuestion.requireNote !== 'boolean') updatedQuestion.requireNote = false;
        }
        return updatedQuestion;
      }
      return q;
    });
    applyEditorStructure(normalizeQuestionConditionals(updatedQuestions), questionPages);
    if (error) setError(null);
  };

  const setQuestionConditional = (id, conditionalConfig, options = {}) => {
    const currentQuestion = questions.find((question) => String(question.id) === String(id));
    const previousDependsOn = currentQuestion?.conditional?.dependsOn ? String(currentQuestion.conditional.dependsOn) : null;
    const nextDependsOn = conditionalConfig?.dependsOn ? String(conditionalConfig.dependsOn) : null;
    let updatedQuestions = questions.map((question) => {
      if (String(question.id) !== String(id)) return question;
      const sanitized = sanitizeConditionalConfig(conditionalConfig);
      if (!sanitized) {
        const { conditional, ...rest } = question;
        return rest;
      }
      return { ...question, conditional: sanitized };
    });

    if (nextDependsOn && !options?.skipReorder && previousDependsOn !== nextDependsOn) {
      updatedQuestions = placeQuestionAfterDependency(updatedQuestions, id, conditionalConfig.dependsOn);
    }

    applyEditorStructure(normalizeQuestionConditionals(updatedQuestions), questionPages);
    if (error) setError(null);
  };

  const handleOptionChange = (qId, optIndex, optText) => {
    const question = questions.find(q => q.id === qId);
    if (question && question.options) {
      updateQuestion(qId, { options: question.options.map((opt, index) => index === optIndex ? optText : opt) });
    }
  };

  const handleOptionScoreChange = (qId, optIndex, score) => {
    const question = questions.find(q => q.id === qId);
    if (question) {
      const currentScores = question.optionScores || (question.options || []).map(() => 0);
      const newScores = currentScores.map((s, index) => index === optIndex ? (score === '' ? 0 : Number(score)) : s);
      updateQuestion(qId, { optionScores: newScores });
    }
  };

  const addOption = (qId) => {
    const question = questions.find(q => q.id === qId);
    if (question && question.options) {
      const updates = { options: [...question.options, ''] };
      if (question.optionScores) {
        updates.optionScores = [...question.optionScores, 0];
      }
      updateQuestion(qId, updates);
    }
  };

  const removeOption = (qId, optIndex) => {
    const question = questions.find(q => q.id === qId);
    if (question && question.options) {
      const updates = { options: question.options.filter((_, index) => index !== optIndex) };
      if (question.optionScores) {
        updates.optionScores = question.optionScores.filter((_, index) => index !== optIndex);
      }
      updateQuestion(qId, updates);
    }
  };

  const removeQuestion = (id) => {
    const filteredQuestions = questions.filter((q) => String(q.id) !== String(id));
    applyEditorStructure(normalizeQuestionConditionals(filteredQuestions), questionPages);
    if (error) setError(null);
  };

  // Default questions management functions
  const addDefaultQuestion = (type = 'text') => {
    const needsOptions = type === 'single-choice' || type === 'multiple-choice' || type === 'ranking' || type === 'matrix' || type === 'combobox';
    setDefaultQuestions(normalizeQuestionConditionals([
      ...defaultQuestions,
      {
        id: `default_${Date.now()}`,
        type,
        text: '',
        description: '',
        answerType: type === 'image' ? 'text' : undefined,
        wordLimit: type === 'text' ? 'no-limit' : undefined,
        options: needsOptions ? (type === 'ranking' ? ['', ''] : ['']) : [],
        rowPlaceholder: type === 'matrix' ? 'Eintrag' : (type === 'combobox' ? 'Eingabe...' : undefined),
        matrixMode: type === 'matrix' ? 'predefined' : undefined,
        rowLabels: type === 'matrix' ? [] : undefined,
        matrixAllowAdditionalRows: type === 'matrix' ? false : undefined,
        suggestions: type === 'combobox' ? [] : undefined,
        rowInputLabel: type === 'combobox' ? '' : undefined,
        columnLabel: type === 'combobox' ? '' : undefined,
        comboboxMode: type === 'combobox' ? 'predefined' : undefined,
        predefinedEntries: type === 'combobox' ? [] : undefined,
        comboboxAllowAdditionalEntries: type === 'combobox' ? false : undefined,
        acceptedFileTypes: type === 'file-upload' ? ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'] : undefined,
        maxFileSize: type === 'file-upload' ? 10 : undefined,
        requireNote: type === 'file-upload' ? false : undefined
      }
    ]));
    if (error) setError(null);
  };

  const updateDefaultQuestion = (id, newProps) => {
    const updatedDefaultQuestions = defaultQuestions.map((q) => {
      if (q.id === id) {
        const updatedQuestion = { ...q, ...newProps };
        if (newProps.type && (newProps.type === 'single-choice' || newProps.type === 'multiple-choice' || newProps.type === 'ranking' || newProps.type === 'matrix' || newProps.type === 'combobox')) {
          if (!updatedQuestion.options || !Array.isArray(updatedQuestion.options)) {
            updatedQuestion.options = newProps.type === 'ranking' ? ['', ''] : [''];
          }
          if (newProps.type === 'matrix') {
            if (!updatedQuestion.rowPlaceholder) updatedQuestion.rowPlaceholder = 'Eintrag';
            if (!updatedQuestion.matrixMode) updatedQuestion.matrixMode = 'predefined';
            if (!Array.isArray(updatedQuestion.rowLabels)) updatedQuestion.rowLabels = [];
            if (typeof updatedQuestion.matrixAllowAdditionalRows !== 'boolean') updatedQuestion.matrixAllowAdditionalRows = false;
          }
          if (newProps.type === 'combobox') {
            if (!Array.isArray(updatedQuestion.suggestions)) updatedQuestion.suggestions = [];
            if (updatedQuestion.rowInputLabel == null) updatedQuestion.rowInputLabel = '';
            if (updatedQuestion.columnLabel == null) updatedQuestion.columnLabel = '';
            if (!updatedQuestion.rowPlaceholder) updatedQuestion.rowPlaceholder = 'Eingabe...';
            if (!updatedQuestion.comboboxMode) updatedQuestion.comboboxMode = 'predefined';
            if (!Array.isArray(updatedQuestion.predefinedEntries)) updatedQuestion.predefinedEntries = [];
            if (typeof updatedQuestion.comboboxAllowAdditionalEntries !== 'boolean') updatedQuestion.comboboxAllowAdditionalEntries = false;
          }
        }
        if (newProps.type === 'file-upload') {
          if (!Array.isArray(updatedQuestion.acceptedFileTypes)) updatedQuestion.acceptedFileTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/png', 'image/jpeg'];
          if (typeof updatedQuestion.maxFileSize !== 'number') updatedQuestion.maxFileSize = 10;
          if (typeof updatedQuestion.requireNote !== 'boolean') updatedQuestion.requireNote = false;
        }
        return updatedQuestion;
      }
      return q;
    });
    setDefaultQuestions(normalizeQuestionConditionals(updatedDefaultQuestions));
    if (error) setError(null);
  };

  const setDefaultQuestionConditional = (id, conditionalConfig, options = {}) => {
    const currentQuestion = defaultQuestions.find((question) => String(question.id) === String(id));
    const previousDependsOn = currentQuestion?.conditional?.dependsOn ? String(currentQuestion.conditional.dependsOn) : null;
    const nextDependsOn = conditionalConfig?.dependsOn ? String(conditionalConfig.dependsOn) : null;
    let updatedDefaultQuestions = defaultQuestions.map((question) => {
      if (String(question.id) !== String(id)) return question;
      const sanitized = sanitizeConditionalConfig(conditionalConfig);
      if (!sanitized) {
        const { conditional, ...rest } = question;
        return rest;
      }
      return { ...question, conditional: sanitized };
    });

    if (nextDependsOn && !options?.skipReorder && previousDependsOn !== nextDependsOn) {
      updatedDefaultQuestions = placeQuestionAfterDependency(updatedDefaultQuestions, id, conditionalConfig.dependsOn);
    }

    setDefaultQuestions(normalizeQuestionConditionals(updatedDefaultQuestions));
    if (error) setError(null);
  };

  const handleDefaultOptionChange = (qId, optIndex, optText) => {
    const question = defaultQuestions.find(q => q.id === qId);
    if (question && question.options) {
      updateDefaultQuestion(qId, {
        options: question.options.map((opt, index) =>
          index === optIndex ? optText : opt
        )
      });
    }
  };

  const handleDefaultOptionScoreChange = (qId, optIndex, score) => {
    const question = defaultQuestions.find(q => q.id === qId);
    if (question) {
      const currentScores = question.optionScores || (question.options || []).map(() => 0);
      const newScores = currentScores.map((s, index) => index === optIndex ? (score === '' ? 0 : Number(score)) : s);
      updateDefaultQuestion(qId, { optionScores: newScores });
    }
  };

  const addDefaultOption = (qId) => {
    const question = defaultQuestions.find(q => q.id === qId);
    if (question && question.options) {
      const updates = { options: [...question.options, ''] };
      if (question.optionScores) {
        updates.optionScores = [...question.optionScores, 0];
      }
      updateDefaultQuestion(qId, updates);
    }
  };

  const removeDefaultOption = (qId, optIndex) => {
    const question = defaultQuestions.find(q => q.id === qId);
    if (question && question.options) {
      const updates = { options: question.options.filter((_, index) => index !== optIndex) };
      if (question.optionScores) {
        updates.optionScores = question.optionScores.filter((_, index) => index !== optIndex);
      }
      updateDefaultQuestion(qId, updates);
    }
  };

  const removeDefaultQuestion = (id) => {
    const filteredDefaultQuestions = defaultQuestions.filter((q) => String(q.id) !== String(id));
    setDefaultQuestions(normalizeQuestionConditionals(filteredDefaultQuestions));
    if (error) setError(null);
  };

  return {
    addQuestion,
    updateQuestion,
    removeQuestion,
    setQuestionConditional,
    handleOptionChange,
    handleOptionScoreChange,
    addOption,
    removeOption,
    addDefaultQuestion,
    updateDefaultQuestion,
    removeDefaultQuestion,
    setDefaultQuestionConditional,
    handleDefaultOptionChange,
    handleDefaultOptionScoreChange,
    addDefaultOption,
    removeDefaultOption,
    normalizeQuestionConditionals
  };
}
